
import { useState, type ReactElement, type ReactNode } from 'react'

import { usePreVendas } from './api/pre-vendas-api'
import { FunilVisual } from './components/funil-visual'

import { ROTULO_ORIGEM, type MatrizSemana, type OrigemPreVendas } from './types'


import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'
import { inteiro, moedaCompacta, percentual } from '@/lib/formato'

const COR_RAG = {
  verde: 'bg-success',
  amarelo: 'bg-warning',
  vermelho: 'bg-destructive',
  pendente: 'bg-muted-foreground',
} as const

function pct(valor: number | null): string {
  return valor === null ? '—' : percentual(valor / 100)
}

function inteiroOuTraco(valor: number | null): string {
  return valor === null ? '—' : inteiro(valor)
}

function valorMeta(indicador: string, valor: number | null): string {
  const ehTaxa = indicador === 'show_rate' || indicador.startsWith('taxa')
  return ehTaxa ? pct(valor) : inteiroOuTraco(valor)
}

/** 'YYYY-MM-DD' → 'DD/MM' sem passar por Date — date-only não tem fuso. */
function diaCurto(dia: string): string {
  return dia.slice(8) + '/' + dia.slice(5, 7)
}

function CartaoTelao({
  titulo,
  children,
  className,
}: {
  titulo: string
  children: ReactNode
  className?: string
}): ReactElement {
  return (
    <div
      className={cn('bg-card/70 border-primary/15 flex flex-col rounded-2xl border p-4', className)}
    >
      <h3 className="mb-3 text-2xl font-semibold">{titulo}</h3>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  )
}

interface ColunaTelao {
  chave: string
  rotulo: string
  alinharDireita?: boolean
}

interface LinhaTelao {
  chave: string
  celulas: Record<string, ReactNode>
  total?: boolean
}

/** Tabela em escala de telão: mesmo contrato da TabelaCompacta, tipografia maior. */
function TabelaTelao({
  colunas,
  linhas,
}: {
  colunas: ColunaTelao[]
  linhas: LinhaTelao[]
}): ReactElement {
  return (
    <table className="w-full text-xl">
      <thead>
        <tr className="border-border border-b">
          {colunas.map((coluna) => (
            <th
              key={coluna.chave}
              className={cn(
                'text-muted-foreground px-2 py-2 text-base font-semibold uppercase tracking-wide',
                coluna.alinharDireita ? 'text-right' : 'text-left',
              )}
            >
              {coluna.rotulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((linha) => (
          <tr
            key={linha.chave}
            className={cn(
              'border-border/60 border-b last:border-0',
              linha.total && 'border-border bg-muted/60 border-t font-semibold',
            )}
          >
            {colunas.map((coluna) => (
              <td
                key={coluna.chave}
                className={cn('px-2 py-2', coluna.alinharDireita && 'text-right tabular-nums')}
              >
                {linha.celulas[coluna.chave]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function MatrizSemanaTelao({ dias, matriz }: { dias: string[]; matriz: MatrizSemana }): ReactElement {
  return (
    <TabelaTelao
      colunas={[
        { chave: 'sdr', rotulo: 'SDR / dia' },
        ...dias.map((dia, i) => ({ chave: `dia-${i}`, rotulo: diaCurto(dia), alinharDireita: true })),
        { chave: 'total', rotulo: 'Total', alinharDireita: true },
      ]}
      linhas={[
        ...matriz.linhas.map((l) => ({
          chave: l.sdr,
          celulas: {
            sdr: l.sdr,
            ...Object.fromEntries(l.porDia.map((qtd, i) => [`dia-${i}`, inteiro(qtd)])),
            total: inteiro(l.total),
          },
        })),
        {
          chave: '__total__',
          total: true,
          celulas: {
            sdr: 'TOTAL',
            ...Object.fromEntries(matriz.totalPorDia.map((qtd, i) => [`dia-${i}`, inteiro(qtd)])),
            total: inteiro(matriz.total),
          },
        },
      ]}
    />
  )
}


function LinhaAgenda({
  rotulo,
  valor,
  atencao,
}: {
  rotulo: string
  valor: number
  atencao?: boolean
}): ReactElement {
  return (
    <div className="border-border/60 flex items-center justify-between gap-3 border-b py-3 last:border-b-0">
      <span className={cn('text-xl', atencao && 'text-warning font-medium')}>{rotulo}</span>
      <span
        className={cn('font-serif text-3xl tabular-nums', atencao ? 'text-warning' : 'text-primary')}
      >
        {inteiro(valor)}
      </span>
    </div>
  )
}

/**
 * O diário de pré-vendas em escala de telão (canvas 2500×1400 escalado pelo
 * PalcoFit): KPIs do dia, as duas matrizes da semana, funil do mês, desempenho
 * por SDR, metas com farol e a agenda de R1 — o relatório diário num slide só.
 */
// Telas internas do slide: a visão do dia e as tabelas de pipeline/closers.
const ABAS_TELA = [
  { tela: 'dia', rotulo: 'Visão do dia' },
  { tela: 'pipeline', rotulo: 'Pipeline do mês' },
] as const

type TelaPreVendas = (typeof ABAS_TELA)[number]['tela']

export function BlocoPreVendasDenso({
  data,
  origem = 'inbound',
}: { data?: string; origem?: OrigemPreVendas } = {}): ReactElement {
  const [tela, setTela] = useState<TelaPreVendas>('dia')
  const { data: resultado, isLoading, isError } = usePreVendas(data, origem)
  const relatorio = resultado?.relatorio ?? null

  if (isError) {
    return <EmptyState title="Pré-vendas indisponível" description="A coleta no HubSpot falhou." />
  }
  if (isLoading || !resultado) {
    return <Skeleton className="h-64" />
  }
  if (!resultado.configurado || !relatorio) {
    return (
      <EmptyState
        title="Pipeline de prospecção não configurado"
        description={resultado.pendencia ?? 'Configure o HubSpot para ativar o painel.'}
      />
    )
  }

  const k = relatorio.kpisDia
  const kpis: { rotulo: string; valor: string; detalhe?: string }[] = [
    { rotulo: 'Leads trabalhados', valor: inteiro(k.trabalhados) },
    { rotulo: 'Novos leads', valor: inteiro(k.novosLeads) },
    ...(k.taxaAgendamento === null
      ? [{ rotulo: 'R1 agendadas', valor: inteiro(k.r1Agendadas) }]
      : [
          {
            rotulo: 'R1 agendadas',
            valor: inteiro(k.r1Agendadas),
            detalhe: `Taxa: ${pct(k.taxaAgendamento)}`,
          },
        ]),
    { rotulo: 'R1 realizadas', valor: inteiro(k.r1Realizadas) },
    { rotulo: 'Em nutrição', valor: inteiro(k.emNutricao) },
    ...(k.taxaPerda === null
      ? [{ rotulo: 'Perdidos', valor: inteiro(k.perdidos) }]
      : [{ rotulo: 'Perdidos', valor: inteiro(k.perdidos), detalhe: `Perda: ${pct(k.taxaPerda)}` }]),
    { rotulo: 'No show', valor: inteiro(k.noShow) },
    ...(k.valorVendasInbound === null
      ? [{ rotulo: 'Vendas (mês)', valor: inteiroOuTraco(k.vendasInbound) }]
      : [
          {
            rotulo: 'Vendas (mês)',
            valor: inteiroOuTraco(k.vendasInbound),
            detalhe: moedaCompacta(k.valorVendasInbound),
          },
        ]),
  ]

  return (
    // Altura MÍNIMA, não fixa: tabela alta (ex.: 14 closers) cresce o canvas e
    // o PalcoFit re-escala pra caber — com altura fixa ela vazava do slide.
    <div className="flex min-h-[1400px] w-full flex-col gap-3 p-1">
      <div className="flex items-start gap-3">
        <div className="bg-primary h-14 w-2 shrink-0 rounded" aria-hidden />
        <div>
          <h1 className="font-serif text-5xl font-normal leading-none tracking-tight">
            {ROTULO_ORIGEM[origem]} — diário
          </h1>
          <p className="text-muted-foreground mt-2 text-xl">
            {diaCurto(relatorio.dataReferencia)}/{relatorio.dataReferencia.slice(0, 4)} · SDRs:{' '}
            {relatorio.equipe.join(' · ')}
            {resultado.fonte === 'snapshot' ? ' · dia encerrado (números congelados)' : ''}
          </p>
        </div>
        <span className="text-primary ml-auto self-center font-serif text-3xl tracking-[0.35em]">
          ENJOY BUSINESS
        </span>
      </div>

      <div className="grid shrink-0 grid-cols-8 gap-3">
        {kpis.map((kpi) => (
          <div
            key={kpi.rotulo}
            className="bg-card/70 border-primary/15 flex flex-col justify-center rounded-xl border px-4 py-4"
          >
            <p className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              {kpi.rotulo}
            </p>
            <p className="text-primary mt-1 font-serif text-4xl leading-none">{kpi.valor}</p>
            {kpi.detalhe ? (
              <p className="text-muted-foreground mt-1.5 text-base">{kpi.detalhe}</p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="flex shrink-0 gap-3">
        {ABAS_TELA.map((aba) => (
          <button
            key={aba.tela}
            type="button"
            onClick={() => setTela(aba.tela)}
            aria-pressed={tela === aba.tela}
            className={cn(
              'rounded-lg border px-6 py-3 text-xl font-medium transition-colors',
              tela === aba.tela
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {tela === 'dia' ? (
        <>
      <div className="grid flex-1 grid-cols-[1.1fr_1.1fr_0.9fr] gap-3">
        <CartaoTelao titulo="Reuniões agendadas — semana">
          <MatrizSemanaTelao dias={relatorio.semana.dias} matriz={relatorio.semana.agendadas} />
        </CartaoTelao>
        <CartaoTelao titulo="Reuniões realizadas — semana">
          <MatrizSemanaTelao dias={relatorio.semana.dias} matriz={relatorio.semana.realizadas} />
        </CartaoTelao>
        <CartaoTelao titulo="Funil do mês">
          <FunilVisual passos={relatorio.funilMes.passos} telao />
        </CartaoTelao>
      </div>

      <div className="grid flex-1 grid-cols-[1.4fr_1.2fr_0.8fr] gap-3">
        <CartaoTelao titulo="Desempenho da equipe (dia)">
          <TabelaTelao
            colunas={[
              { chave: 'sdr', rotulo: 'SDR' },
              { chave: 'recebidos', rotulo: 'Receb.', alinharDireita: true },
              { chave: 'trabalhados', rotulo: 'Trab.', alinharDireita: true },
              { chave: 'agendadas', rotulo: 'R1 agend.', alinharDireita: true },
              { chave: 'realizadas', rotulo: 'R1 realiz.', alinharDireita: true },
              { chave: 'noShow', rotulo: 'No show', alinharDireita: true },
              { chave: 'perdidos', rotulo: 'Perdidos', alinharDireita: true },
              { chave: 'taxa', rotulo: 'Taxa', alinharDireita: true },
            ]}
            linhas={relatorio.equipeDia.map((l) => ({
              chave: l.sdr,
              celulas: {
                sdr: l.sdr,
                recebidos: inteiro(l.recebidos),
                trabalhados: inteiro(l.trabalhados),
                agendadas: inteiro(l.r1Agendadas),
                realizadas: inteiro(l.r1Realizadas),
                noShow: inteiro(l.noShow),
                perdidos: inteiro(l.perdidos),
                taxa: pct(l.taxaAgendamento),
              },
            }))}
          />
        </CartaoTelao>

        <CartaoTelao titulo="Metas vs. realizado (individuais)">
          <TabelaTelao
            colunas={[
              { chave: 'indicador', rotulo: 'Indicador' },
              { chave: 'meta', rotulo: 'Meta', alinharDireita: true },
              ...(relatorio.metas[0]?.porSdr ?? []).map((p, i) => ({
                chave: `sdr-${i}`,
                rotulo: p.sdr,
                alinharDireita: true,
              })),
              { chave: 'total', rotulo: 'Total', alinharDireita: true },
            ]}
            linhas={relatorio.metas.map((m) => ({
              chave: m.indicador,
              celulas: {
                indicador: m.rotulo,
                meta:
                  m.metaProporcional !== null
                    ? `${valorMeta(m.indicador, m.meta)} · hoje ${valorMeta(m.indicador, m.metaProporcional)}`
                    : valorMeta(m.indicador, m.meta),
                ...Object.fromEntries(
                  m.porSdr.map((linha, i) => [
                    `sdr-${i}`,
                    <span key={linha.sdr} className="flex items-center justify-end gap-2">
                      {valorMeta(m.indicador, linha.realizadoMes ?? linha.realizadoDia)}
                      <span
                        className={cn('h-4 w-4 shrink-0 rounded-full', COR_RAG[linha.rag])}
                        role="img"
                        aria-label={linha.rag === 'pendente' ? 'sem dado' : linha.rag}
                      />
                    </span>,
                  ]),
                ),
                total: (
                  <span className="flex items-center justify-end gap-2">
                    {valorMeta(m.indicador, m.total.realizadoMes ?? m.total.realizadoDia)}
                    <span
                      className={cn('h-4 w-4 shrink-0 rounded-full', COR_RAG[m.total.rag])}
                      role="img"
                      aria-label={m.total.rag === 'pendente' ? 'sem dado' : m.total.rag}
                    />
                  </span>
                ),
              },
            }))}
          />
        </CartaoTelao>

        <CartaoTelao titulo="R1 — próximos dias">
          <div className="flex h-full flex-col justify-center">
            <LinhaAgenda rotulo="Amanhã" valor={relatorio.agendaFutura.amanha} />
            <LinhaAgenda rotulo="Próximos 7 dias" valor={relatorio.agendaFutura.seteDias} />
            <LinhaAgenda rotulo="Este mês" valor={relatorio.agendaFutura.esteMes} />
            <LinhaAgenda rotulo="Próximo mês" valor={relatorio.agendaFutura.proximoMes} />
            <LinhaAgenda
              rotulo="Sem data no CRM"
              valor={relatorio.agendaFutura.semData}
              atencao={relatorio.agendaFutura.semData > 0}
            />
          </div>
        </CartaoTelao>
      </div>
        </>
      ) : (
        <>
          <div className="grid flex-1 grid-cols-[1.2fr_1fr] gap-3">
            <CartaoTelao titulo="Pipeline de prospecção — mês">
              <TabelaTelao
                colunas={[
                  { chave: 'etapa', rotulo: 'Etapa do funil' },
                  ...relatorio.pipelineMes.sdrs.map((sdr, i) => ({
                    chave: `sdr-${i}`,
                    rotulo: sdr,
                    alinharDireita: true,
                  })),
                  { chave: 'total', rotulo: 'Total', alinharDireita: true },
                  { chave: 'pct', rotulo: '% do total', alinharDireita: true },
                ]}
                linhas={[
                  ...relatorio.pipelineMes.linhas.map((l) => ({
                    chave: l.etapa,
                    celulas: {
                      etapa: l.etapa,
                      ...Object.fromEntries(l.porSdr.map((qtd, i) => [`sdr-${i}`, inteiro(qtd)])),
                      total: inteiro(l.total),
                      pct: pct(l.pct),
                    },
                  })),
                  {
                    chave: '__total__',
                    total: true,
                    celulas: {
                      etapa: 'TOTAL',
                      ...Object.fromEntries(relatorio.pipelineMes.sdrs.map((_, i) => [`sdr-${i}`, '—'])),
                      total: inteiro(relatorio.pipelineMes.totalGeral),
                      pct: '100%',
                    },
                  },
                ]}
              />
            </CartaoTelao>
            <CartaoTelao titulo="Funil de leads trabalhados no dia">
              <TabelaTelao
                colunas={[
                  { chave: 'etapa', rotulo: 'Etapa do funil' },
                  { chave: 'qtd', rotulo: 'Qtd', alinharDireita: true },
                  { chave: 'pct', rotulo: '% do total', alinharDireita: true },
                ]}
                linhas={relatorio.funilDia.map((l) => ({
                  chave: l.etapa,
                  celulas: { etapa: l.etapa, qtd: inteiro(l.qtd), pct: pct(l.pct) },
                }))}
              />
            </CartaoTelao>
          </div>

        </>
      )}
    </div>
  )
}
